import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { findBlurRelatedTarget, shouldIgnoreFocusEvent } from '../../utils/DomUtil';
import { InputAttrs } from '../../utils/HtmlAttrs';
import { TextFieldBaseProps } from './TextFieldBase';

export interface TextFieldInputProps extends InputAttrs,
  Pick<TextFieldBaseProps, 'handleChange' | 'inputRef' | 'testId'> { }

const InputStyleName = TypeStyle.style({
  '-webkit-appearance': 'none', // TFSID 719780: hide inner shadow on iOS text boxes
  boxSizing: 'border-box',
  fontFamily: 'inherit', // override user-agent default stylesheet
});

export class TextFieldInputWidget extends React.Component<TextFieldInputProps> {
  public static defaultProps: Pick<TextFieldInputProps, 'type' | 'value'> = {
    type: 'text',
    value: '',
  };

  private isComposing: boolean = false;

  private handleBlur: React.FocusEventHandler<HTMLInputElement> = e => {
    e.relatedTarget = findBlurRelatedTarget(e)!; // polyfill since React refuses to :(
    this.props.onBlur?.(e);
  }

  private handleChange: React.FormEventHandler<HTMLInputElement> = e => {
    if (this.props.disabled || !this.props.handleChange) { return; }

    this.props.handleChange((e.target as HTMLInputElement).value, this.isComposing);
  }

  private handleComposition: React.CompositionEventHandler<HTMLInputElement> = e => {
    if (e.type === 'compositionend') {
      this.isComposing = false;
      this.handleChange(e);
    } else {
      this.isComposing = true;
    }
  }

  private handleFocus: React.FocusEventHandler<HTMLInputElement> = e => {
    if (shouldIgnoreFocusEvent(e)) { return; }

    this.props.onFocus?.(e);
  }

  private handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (this.props.disabled || this.isComposing) { return; }

    this.props.onKeyDown?.(e);
  }

  private handleKeyUp: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (this.props.disabled || this.isComposing) { return; }

    this.props.onKeyUp?.(e);
  }

  public render(): JSX.Element {
    const { className, inputRef, handleChange, testId, ...htmlAttrs } = this.props;

    return (
      <input
        {...htmlAttrs}
        className={TypeStyle.classes(className, InputStyleName)}
        data-tb-test-id={testId + '-TextInput'}
        onBlur={this.handleBlur}
        onChange={this.handleChange}
        onCompositionEnd={this.handleComposition}
        onCompositionStart={this.handleComposition}
        onCompositionUpdate={this.handleComposition}
        onFocus={this.handleFocus}
        onKeyDown={this.handleKeyDown}
        onKeyUp={this.handleKeyUp}
        ref={inputRef}
      />
    );
  }
}
